import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sun, Moon, LogOut, Settings, LayoutDashboard, Search, Menu, X, History, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import { useDocsI18n } from '../../i18n';

export function Navbar() {
    const { user, login, logout } = useAuth();
    const { lang, toggle, t } = useDocsI18n();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[90%] max-w-4xl rounded-full border border-border/50 bg-background/60 backdrop-blur-xl px-6 py-3 shadow-2xl z-50">
            <div className="flex items-center justify-between">
                {/* Logo */}
                <Link 
                    to="/" 
                    className="flex items-center gap-2.5 group"
                    data-testid="logo-link"
                >
                    <div className="w-8 h-8 rounded-lg titanv-gradient flex items-center justify-center font-mono font-bold text-white text-sm shadow-lg shadow-primary/30 group-hover:scale-105 transition-transform">
                        &gt;_
                    </div>
                    <span className="font-display font-bold text-lg tracking-tight">TitanV</span>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center gap-4">
                    <Link 
                        to="/search" 
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="search-link"
                    >
                        <Search className="w-4 h-4" />
                        <span className="text-sm">{t('search')}</span>
                    </Link>

                    <Link 
                        to="/changelog" 
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="changelog-link"
                    >
                        <History className="w-4 h-4" />
                        <span className="text-sm">{t('changelog')}</span>
                    </Link>

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleTheme}
                        className="rounded-full"
                        data-testid="theme-toggle"
                    >
                        {theme === 'dark' ? (
                            <Sun className="w-4 h-4" />
                        ) : (
                            <Moon className="w-4 h-4" />
                        )}
                    </Button>

                    <Button variant="ghost" size="sm" onClick={toggle} className="rounded-full font-mono text-xs" data-testid="docs-lang">
                        {lang === 'de' ? 'EN' : 'DE'}
                    </Button>

                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    className="flex items-center gap-2 rounded-full"
                                    data-testid="user-menu-trigger"
                                >
                                    {user.avatar ? (
                                        <img 
                                            src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                                            alt={user.username}
                                            className="w-6 h-6 rounded-full"
                                        />
                                    ) : (
                                        <div className="w-6 h-6 rounded-full titanv-gradient flex items-center justify-center text-xs font-bold text-white">
                                            {user.username[0].toUpperCase()}
                                        </div>
                                    )}
                                    <span className="text-sm font-medium">{user.username}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                {user.is_admin && (
                                    <>
                                        <DropdownMenuItem 
                                            onClick={() => navigate('/admin')}
                                            data-testid="admin-menu-item"
                                        >
                                            <LayoutDashboard className="w-4 h-4 mr-2" />
                                            Admin Dashboard
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}
                                <DropdownMenuItem 
                                    onClick={logout}
                                    data-testid="logout-menu-item"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    {t('logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Button 
                            onClick={login}
                            className="rounded-full titanv-gradient text-white font-semibold border-0 hover:opacity-90 hover:scale-[1.02] transition-all shadow-lg shadow-primary/20 gap-2"
                            data-testid="discord-login-btn"
                        >
                            {t('login')}
                            <ArrowRight className="w-4 h-4" />
                        </Button>
                    )}
                </div>

                {/* Mobile Menu Button */}
                <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden rounded-full"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    data-testid="mobile-menu-btn"
                >
                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
            </div>

            {/* Mobile Menu */}
            <div className={cn(
                "md:hidden absolute top-full left-0 right-0 mt-2 p-4 rounded-2xl border border-border/50 bg-background/95 backdrop-blur-xl",
                mobileMenuOpen ? "block animate-fade-in" : "hidden"
            )}>
                <div className="flex flex-col gap-2">
                    <Link 
                        to="/search" 
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        <Search className="w-4 h-4" />
                        <span>{t('search')}</span>
                    </Link>
                    <Link 
                        to="/changelog" 
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        <History className="w-4 h-4" />
                        <span>{t('changelog')}</span>
                    </Link>
                    <button 
                        onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                    >
                        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        <span>{theme === 'dark' ? t('light') : t('dark')}</span>
                    </button>
                    {user ? (
                        <>
                            {user.is_admin && (
                                <Link 
                                    to="/admin" 
                                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <LayoutDashboard className="w-4 h-4" />
                                    <span>{t('admin')}</span>
                                </Link>
                            )}
                            <button 
                                onClick={() => { logout(); setMobileMenuOpen(false); }}
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                            >
                                <LogOut className="w-4 h-4" />
                                <span>{t('logout')}</span>
                            </button>
                        </>
                    ) : (
                        <Button 
                            onClick={() => { login(); setMobileMenuOpen(false); }}
                            className="w-full titanv-gradient text-white font-semibold border-0 gap-2"
                        >
                            {t('loginDiscord')}
                            <ArrowRight className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </div>
        </nav>
    );
}
